import React, {useEffect, useState} from "react";
import RequireAuth from "routes/RequireAuth";
import AppLayout from "layouts/AppLayout";
import {
  Alert,
  Button,
  CircularProgress,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import {useParams} from "react-router-dom";
import {useCurrentProject} from "hooks/projects";
import {isEmpty} from "lodash";
import WeatherScenarioAPI, {IWeatherScenario} from "api/scenarios";
import {Save} from "@mui/icons-material";
import {enqueueSnackbar} from "notistack";
import {axios500Error, loadingError, successMessage} from "content/adsystem/google_ads";
import GoogleAdsApi, {
  IGoogleAdsCampaign,
  IGoogleAdsCampaignRemote,
  IGoogleAdsCreatePayload
} from "../../../api/google_ads";

const GoogleAdsEdit = () => {
  const [campaign, setCampaign] = useState<IGoogleAdsCampaign | null>();
  const [campaignPayload, setCampaignPayload] = useState<
    Partial<IGoogleAdsCreatePayload>
  >({});
  const [remoteCampaigns, setRemoteCampaigns] = useState<
    IGoogleAdsCampaignRemote[]
  >([]);
  const [weatherScenarios, setWeatherScenarios] = useState<IWeatherScenario[]>(
    []
  );
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [loadingRemote, setLoadingRemote] = useState(false);
  const [loadingScenarios, setLoadingScenarios] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentProject] = useCurrentProject();

  const loadCampaign = async () => {
    try {
      setLoading(true);
      const campaign = await GoogleAdsApi.getCampaign(currentProject.id, id as string);
      setCampaign(campaign);
      setCampaignPayload({
        campaign_id: campaign.campaign_id,
        campaign_name: campaign.name,
        advertising_channel_type: campaign.advertising_channel_type,
        status: campaign.status,
        scenario_id: campaign.scenario.id,
      });
    } catch (e) {
      enqueueSnackbar(loadingError, { variant: "error" });
    } finally {
      setLoading(false);
    }
  }

  const loadWeatherScenarios = async () => {
    try {
      setLoadingScenarios(true);
      const response = await WeatherScenarioAPI.listScenarios(
        currentProject.id,
        false
      );
      setWeatherScenarios(response);
    } finally {
      setLoadingScenarios(false);
    }
  };

  const loadRemoteCampaigns = async () => {
    try {
      setLoadingRemote(true);
      const remoteCampaigns = await GoogleAdsApi.listRemoteCampaigns(
        currentProject.id
      );
      setRemoteCampaigns(remoteCampaigns);
    } finally {
      setLoadingRemote(false);
    }
  };

  const saveCampaign = async () => {
    try {
      setSaving(true);
      if (!isEmpty(campaignPayload)) {
        const response = await GoogleAdsApi.updateCampaign(currentProject.id, campaign?.campaign_id as string, campaignPayload as IGoogleAdsCreatePayload);
        setCampaign(response);
        enqueueSnackbar(successMessage, { variant: "success" });
      }
    } catch (e) {
      enqueueSnackbar(axios500Error, { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleCampaignChange = (e: SelectChangeEvent) => {
    const filteredCampaign = remoteCampaigns.filter(
      (campaign) => campaign.campaign_id == e.target.value
    )[0];
    setCampaignPayload((prevState) => ({
      ...prevState,
        campaign_id: filteredCampaign.campaign_id,
        campaign_name: filteredCampaign.name,
        advertising_channel_type: filteredCampaign.advertising_channel_type,
        status: filteredCampaign.status,
    }));
  };

  const handleScenarioChange = (e: SelectChangeEvent) => {
    setCampaignPayload((prevState) => ({
      ...prevState,
      scenario_id: Number(e.target.value),
    }));
  };

  useEffect(() => {
    if (!isEmpty(currentProject)) {
      loadCampaign();
      loadRemoteCampaigns();
      loadWeatherScenarios();
    }
  }, [currentProject.id]);

  const campaigns = remoteCampaigns.map((campaign) => (
    <MenuItem key={campaign.campaign_id} value={campaign.campaign_id}>
      {campaign.name} (<small>ID {campaign.campaign_id}</small>)
    </MenuItem>
  ));

  const scenarios = weatherScenarios.map((scenario) => (
    <MenuItem key={scenario.id} value={scenario.id}>
      {scenario.name}
    </MenuItem>
  ));

  return (
    <RequireAuth>
      <AppLayout>
        <Grid container rowSpacing={2}>
          {loading && <CircularProgress />}
          <React.Fragment>
            <Grid item xs={12}>
              <Typography variant="h4">
                Assign weather scenario to a Google Ads campaign
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Alert severity="info">
                Select a campaign and a weather scenario from a list to be
                managed. AdStorm will automatically update geotargeting to match
                locations where the weather you defined is taking place.
              </Alert>
            </Grid>
            <Grid item xs={12}>
              {loadingRemote && <CircularProgress />}
              {!loadingRemote && !loading && (
                <React.Fragment>
                  <InputLabel id="campaign">Campaign</InputLabel>
                  <Select id="campaign" value={campaign?.campaign_id as unknown as string} onChange={handleCampaignChange}>
                    {campaigns}
                  </Select>
                </React.Fragment>
              )}
              {loadingScenarios && <CircularProgress />}
              {!loadingScenarios && !loading && (
                <React.Fragment>
                  <InputLabel id="scenarios">Weather scenario</InputLabel>
                  <Select id="scenarios" value={campaign?.scenario.id as unknown as string} onChange={handleScenarioChange}>
                    {scenarios}
                  </Select>
                </React.Fragment>
              )}
            </Grid>
          </React.Fragment>

          <Grid item xs={12}>
            {saving && <CircularProgress />}
            <Button
              variant="contained"
              startIcon={<Save />}
              onClick={saveCampaign}
            >
              Save
            </Button>
          </Grid>
        </Grid>
      </AppLayout>
    </RequireAuth>
  );
};

export default GoogleAdsEdit;
